import React from "react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Badge from "@/components/foundation/Badge";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";

const SECTIONS = [
  {
    id: "acceptance",
    heading: "Acceptance of Terms",
    body: [
      "By accessing or using the ZenMe Capital website, mobile application, or related services (collectively, the \"Services\"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Services.",
    ],
  },
  {
    id: "description",
    heading: "Description of Service",
    body: [
      "ZenMe Capital provides an AI-powered business funding readiness platform. Current services include the ZenMe marketing website, the ZenScore engine, and the merchant onboarding and funding application platform.",
      "Additional services — including a self-serve marketplace, partner APIs, and embedded finance products — are under development and will be governed by updated terms upon release.",
    ],
  },
  {
    id: "eligibility",
    heading: "Eligibility",
    body: [
      "The Services are intended for use by US-based businesses and by individuals who are at least 18 years of age and legally authorized to act on behalf of their business.",
    ],
  },
  {
    id: "account",
    heading: "Account Responsibilities",
    body: [
      "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate, current, and complete information at all times and to promptly update your information as needed.",
    ],
  },
  {
    id: "prohibited",
    heading: "Prohibited Uses",
    body: [
      "You agree not to use the Services to:",
    ],
    list: [
      "Submit false, misleading, or fraudulent information",
      "Impersonate any person or entity",
      "Attempt to gain unauthorized access to any part of the Services",
      "Interfere with the security, integrity, or performance of the Services",
      "Use the Services to engage in any activity that violates applicable law",
    ],
  },
  {
    id: "financial-disclaimer",
    heading: "Financial Disclaimer",
    body: [
      "ZenMe Capital is a technology and data platform. ZenMe is not a lender and does not directly provide loans, funding, or capital.",
      "ZenMe does not guarantee funding, funding amounts, or funding outcomes. Availability of funding is determined solely by third-party funding partners based on their own underwriting standards, and no representation is made regarding funding terms or turnaround times.",
      "ZenScore is an informational business health and funding readiness indicator. ZenScore is not a credit score, is not a guarantee of funding, and does not constitute financial, legal, or investment advice.",
    ],
  },
  {
    id: "ip",
    heading: "Intellectual Property",
    body: [
      "All content, features, and functionality of the Services — including but not limited to text, graphics, logos, software, and the ZenScore engine and methodology — are the exclusive property of ZenMe Capital and its licensors and are protected by intellectual property laws.",
      "You are granted a limited, non-exclusive, non-transferable license to use the Services for their intended purpose. All other rights are reserved.",
    ],
  },
  {
    id: "liability",
    heading: "Limitation of Liability",
    body: [
      "To the maximum extent permitted by law, ZenMe Capital and its affiliates, officers, employees, and partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Services.",
      "The Services are provided on an \"as is\" and \"as available\" basis. ZenMe makes no warranties, express or implied, regarding the Services beyond those required by law.",
    ],
  },
  {
    id: "governing-law",
    heading: "Governing Law",
    body: [
      "These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws principles. Any disputes will be resolved in the state or federal courts located in Delaware, USA.",
    ],
  },
  {
    id: "changes",
    heading: "Changes to These Terms",
    body: [
      "We may update these Terms of Service from time to time. Material changes will be communicated via the website or in-app. Your continued use of the Services after such changes constitutes acceptance of the updated Terms.",
    ],
  },
  {
    id: "contact-legal",
    heading: "Contact",
    body: [
      "For questions about these Terms of Service, contact legal@zenmecapital.com.",
    ],
  },
];

export default function Terms() {
  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · Terms of Service"
        description="Terms of Service for ZenMe Capital's website, mobile application, and related services. Informational draft — the final version will be published prior to launch."
        path="/terms"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-12 sm:pt-32 sm:pb-16"
        data-testid="terms-hero"
      >
        <GlowOrb color="emerald" size={420} intensity={0.14} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="max-w-3xl">
            <FadeIn>
              <Badge variant="emerald">Legal</Badge>
            </FadeIn>
            <FadeIn delay={0.06}>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Terms of Service
              </h1>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="mt-4 text-sm text-text-muted">
                Effective date: <span className="font-mono">[placeholder — pre-launch]</span>
              </p>
            </FadeIn>
            <FadeIn delay={0.22}>
              <div className="mt-6 rounded-xl border border-brand-emerald/20 bg-brand-emerald/[0.04] p-4 text-sm text-text-secondary">
                <strong className="text-brand-emerald">Notice:</strong> This
                document is provided for informational purposes. A final,
                legally-reviewed Terms of Service will be published prior to
                launch.
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* BODY */}
      <Container className="relative z-10 pb-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <aside className="hidden lg:col-span-3 lg:block">
            <nav className="sticky top-24 flex flex-col gap-2 border-l border-white/[0.06] pl-4">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">
                Contents
              </p>
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-xs text-text-secondary transition-colors hover:text-brand-emerald"
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex flex-col gap-14">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                    {s.heading}
                  </h2>
                  {s.body?.map((p, i) => (
                    <p
                      key={i}
                      className="mt-4 text-sm leading-relaxed text-text-secondary sm:text-base"
                    >
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-4 flex flex-col gap-2">
                      {s.list.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-3 text-sm text-text-secondary"
                        >
                          <span className="mt-2 inline-block h-1 w-1 flex-shrink-0 rounded-full bg-brand-emerald" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </PageLayout>
  );
}
